import { FC, useCallback, useId, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import {
  MDSButton,
  MDSLabel,
  MDSText,
  MDSTextColor,
  MDSTextField,
  MDSTextFieldVariant,
  MDSTextVariant,
  MDSPanelContainer,
} from "@/mds";
import { useAppStore } from "@/store";
import { uuidModule } from "@/modules/uuid";

type Props = Record<string, never>;

export const AudioRecordingCreatePage: FC<Props> = observer(() => {
  const { store } = useAppStore();
  const [isCreating, setIsCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [noteIdValue, setNoteIdValue] = useState("");
  const noteIdInputId = useId();

  const handleNoteIdChange = useCallback(
    (value: string) => {
      setNoteIdValue(value);
      if (errorMessage) {
        setErrorMessage(null);
      }
    },
    [errorMessage]
  );

  const actionLabel = useMemo(
    () => (isCreating ? "Creating…" : "Create audio recording"),
    [isCreating]
  );

  const handleCreate = useCallback(async () => {
    if (isCreating) {
      return;
    }

    const trimmedNoteId = noteIdValue.trim();
    let normalizedNoteId: string | null = null;

    if (trimmedNoteId) {
      try {
        normalizedNoteId = uuidModule.validateAndNormalize({ uuidString: trimmedNoteId });
      } catch (_err) {
        setErrorMessage("Please enter a valid note ID (UUID).");
        return;
      }
    }

    setIsCreating(true);
    setErrorMessage(null);

    try {
      const audioRecording = await store.dexie.repositories.audioRecordings.create({
        data: {
          associatedNoteId: normalizedNoteId ?? null,
        },
      });

      store.navigation.goToAudioRecording({ recordingId: audioRecording.id, openInSameTab: true });
      setNoteIdValue("");
      setIsCreating(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create audio recording";
      setErrorMessage(message);
      setIsCreating(false);
    }
  }, [isCreating, noteIdValue, store]);

  const errorContent = useMemo(() => {
    if (!errorMessage) {
      return null;
    }

    return (
      <MDSText variant={MDSTextVariant.TextSm} color={MDSTextColor.Danger}>
        {errorMessage}
      </MDSText>
    );
  }, [errorMessage]);

  return (
    <MDSPanelContainer>
      <Header>
        <MDSText variant={MDSTextVariant.HeaderXl} numberOfLines={1}>
          Create audio recording
        </MDSText>
        <MDSText variant={MDSTextVariant.TextSm} color={MDSTextColor.Secondary}>
          Generates a new audio recording entry that will appear in your list.
        </MDSText>
      </Header>
      <Actions>
        <FieldGroup>
          <MDSLabel htmlFor={noteIdInputId}>Associated note ID (optional)</MDSLabel>
          <MDSTextField
            id={noteIdInputId}
            value={noteIdValue}
            onChange={handleNoteIdChange}
            placeholder="Paste an existing note UUID"
            variant={MDSTextFieldVariant.Form}
          />
          <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
            Linking the recording to a note lets us surface its progress alongside that note.
          </MDSText>
        </FieldGroup>
        <MDSButton onClick={handleCreate} isDisabled={isCreating}>
          {actionLabel}
        </MDSButton>
        {errorContent}
      </Actions>
    </MDSPanelContainer>
  );
});

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 420px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
